import { format } from 'date-fns';

const apiDbLayout = 'yyyy-MM-dd HH:mm:ss';

export const getNowDBFormat = () => {
  return format(new Date(), apiDbLayout);
}

export const getNow = () => {
  const currentDate = new Date();
  const formattedDate = format(currentDate, apiDbLayout);
  return new Date(formattedDate);
}

export const getLocaleDate = (date: string, digits: number) => {
  if (date) {
    const d = new Date(date)
    return d.toLocaleDateString().substring(0, digits);
  }
  return ''
};

/**
 * Formata uma string de data YYYY-MM-DD (apenas data, sem hora) para dd/mm/yyyy
 * sem conversão de fuso — evita que "1976-03-16" vire 15/03 no Brasil.
 */
export const formatDateOnlyString = (dateStr: string | null): string => {
  if (!dateStr) return '';
  const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return '';
  const [, yyyy, mm, dd] = match;
  return `${dd}/${mm}/${yyyy}`;
};

export const getLocaleDateTime = (date: string) => {
  if (date) {
    const d = new Date(date);

    const localeDate = d.toLocaleDateString();
    
    const localeTime = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    
    return `${localeDate} ${localeTime}`;
  }
  return '';
};


export const formatDateToString = (date: Date) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
  const dd = String(date.getDate()).padStart(2, '0');


  return `${yyyy}-${mm}-${dd}`;
}
export const formatDateTimeToString = (date: Date) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
  const dd = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const ii = String(date.getMinutes()).padStart(2, '0');
  const ss = String(date.getSeconds()).padStart(2, '0');

  return `${yyyy}-${mm}-${dd} ${hh}:${ii}:${ss}`;
}

/** Formato ISO local (YYYY-MM-DDTHH:mm:ss) para payloads de API sem conversão para UTC. */
export const formatDateTimeToISOLocal = (date: Date | null | undefined): string | null => {
  if (date == null) return null;
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const ii = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}T${hh}:${ii}:${ss}`;
};

/**
 * Converte string de data/hora da API em Date para exibição no input datetime-local.
 * O valor no banco é "horário de parede" (sem fuso); a API pode devolver em UTC (ex: ...Z).
 * Esta função interpreta os componentes da string como horário local para exibição correta.
 */
export const parseServiceDateFromAPI = (value: string | null | undefined): Date | null => {
  if (value == null || value === '') return null;
  const s = typeof value === 'string' ? value : String(value);
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(?:Z|[-+]\d{2}:?\d{2})?$/i.exec(s)
    || /^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})(?::(\d{2}))?/.exec(s);
  if (!match) {
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }
  const [, y, m, d, h, min, sec] = match;
  const date = new Date(Number(y), Number(m) - 1, Number(d), Number(h), Number(min) || 0, Number(sec) || 0, 0);
  return isNaN(date.getTime()) ? null : date;
};

export const addOneMinute = (time: string) => {
  // Separar a hora e o minuto da entrada
  const [hours, minutes] = time.split(":").map(Number);

  // Criar um objeto Date baseando-se na hora atual
  const date = new Date();
  date.setHours(hours, minutes, 0, 0); // Configurar a hora e o minuto fornecidos

  // Adicionar um minuto
  date.setMinutes(date.getMinutes() + 1);

  // Formatar de volta no formato "HH:mm"
  const newHours = String(date.getHours()).padStart(2, "0");
  const newMinutes = String(date.getMinutes()).padStart(2, "0");

  return `${newHours}:${newMinutes}`;
}

// Exemplo de uso
const input = "12:59";
const result = addOneMinute(input);


export const formatDateTimezone = (date: Date) => {
  // return date in format 2023-04-16T00:00:00-03:00
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
  const dd = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const ii = String(date.getMinutes()).padStart(2, '0');
  const ss = String(date.getSeconds()).padStart(2, '0');

  // Calculate the timezone offset in hours and minutes
  const timezoneOffset = -date.getTimezoneOffset();
  const tzHours = String(Math.abs(Math.floor(timezoneOffset / 60))).padStart(2, '0');
  const tzMinutes = String(Math.abs(timezoneOffset % 60)).padStart(2, '0');
  const tzSign = timezoneOffset >= 0 ? '+' : '-';

  return `${yyyy}-${mm}-${dd}T${hh}:${ii}:${ss}${tzSign}${tzHours}:${tzMinutes}`;
}

export const getDateByDayAndHour = (dayOfWeek: number, hour: string) => {
  const now = new Date();

  const currentDayOfWeek = now.getDay() === 0 ? 6 : now.getDay() - 1;

  const dayDifference = dayOfWeek - currentDayOfWeek;

  const targetDate = new Date(now);
  targetDate.setDate(now.getDate() + dayDifference);

  const [hours, minutes, seconds] = hour.split(':').map(Number);
  targetDate.setHours(hours, minutes, seconds, 0);

  return formatDateTimeToString(targetDate);
}

export const getDateFromUrl = () => {
  const url = window.location.href;

  const regex = /\/services_schedule\/(\d{4}-\d{2}-\d{2})/;
  const match = url.match(regex);

  return match ? match[1] : null;
}

export const daysOfWeek = [
  
  {
    id: 0,
    description: "Domingo",
  },
  {
    id: 1,
    description: "Segunda",
  },
  {
    id: 2,
    description: "Terça",
  },
  {
    id: 3,
    description: "Quarta",
  },
  {
    id: 4,
    description: "Quinta",
  },
  {
    id: 5,
    description: "Sexta",
  },
  {
    id: 6,
    description: "Sábado",
  }
];